import { Controller } from "@hotwired/stimulus"

// Copies the action's text param to the clipboard with brief button feedback.
// Usage: data-action="clipboard#copy" data-clipboard-text-param="…"
// Icon-only buttons pass data-clipboard-feedback-param="tooltip" so the SVG is
// preserved (flash a daisyUI tooltip instead of replacing the button text).
export default class extends Controller {
  initialize() {
    // Feedback is tracked per BUTTON: one controller scope holds several copy buttons
    // (the row actions menu has three; the detail header two), and a single shared timer
    // let a second copy clear the first button's restore — stranding it on "Copied!".
    this.pending = new Map()
  }

  disconnect() {
    this.pending.forEach(({ timer }) => clearTimeout(timer))
    this.pending.clear()
  }

  // FD-333: CSP-safe replacement for inline onclick="this.select()" on the
  // read-only share-URL field — select all text on click for easy manual copy.
  select(event) {
    event.currentTarget.select()
  }

  async copy(event) {
    event.preventDefault()
    const button = event.currentTarget
    await navigator.clipboard.writeText(event.params.text)
    if (event.params.feedback === "tooltip") {
      this.flash(button, () => {
        const prev = button.getAttribute("data-tip")
        button.setAttribute("data-tip", "Copied!")
        button.classList.add("tooltip-open")
        return () => {
          button.classList.remove("tooltip-open")
          if (prev == null) button.removeAttribute("data-tip")
          else button.setAttribute("data-tip", prev)
        }
      })
    } else {
      // Swap the LABEL's text, never the button's textContent: textContent replaces every
      // child, so an icon inside the button (a bare icon, or the "Copy link" menu row's
      // icon + text) was deleted for good one second later.
      const label = this.labelNode(button)
      if (!label) return
      this.flash(button, () => {
        const original = label.textContent
        label.textContent = "Copied!"
        return () => { label.textContent = original }
      })
    }
  }

  // The node carrying the button's visible label: an explicit [data-clipboard-label] span
  // if the button provides one, else the button's own text node so sibling icons survive.
  // A button with neither is icon-only and gets no text feedback — those pass
  // feedback: "tooltip" instead.
  labelNode(button) {
    return button.querySelector("[data-clipboard-label]") ||
      Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== "")
  }

  // Apply a flash and schedule its undo, one timer per button. Any flash still pending on
  // the SAME button is undone first, so a repeat click captures the real label rather than
  // "Copied!" (which would otherwise stick permanently).
  flash(button, apply) {
    this.undoFlash(button)
    const undo = apply()
    this.pending.set(button, { timer: setTimeout(() => this.undoFlash(button), 1000), undo })
  }

  undoFlash(button) {
    const entry = this.pending.get(button)
    if (!entry) return
    clearTimeout(entry.timer)
    this.pending.delete(button)
    entry.undo()
  }
}
